// src/pages/markets/board/api/boardApi.ts

import { apiGet } from '../../api/apiClient';
import {
    GATED_FIELDS,
    FILTER_WIRE_KEYS,
    type BoardActivityResponse,
    type BoardEventsResponse,
    type BoardFilters,
    type BoardSchema,
    type BoardScoresResponse,
    type BoardSeriesResponse,
    type BoardTradersResponse,
    type BoardRankResponse,
    type BoardEventsByIdsResponse,
    type SortDir,
    type TagMatch,
    type BoardGroupStatsResponse,
} from './boardTypes';

const API_BASE = '/api/screener/board';

export const MAX_EVENT_IDS = 60;
export const MAX_SERIES_MARKETS = 60;
export const MAX_ACTIVITY_EVENT_IDS = 60;

// Empty string is kept, undefined/null are dropped: '' is how the backend is
// told "no filter", which is different from "use the column default".
function qs(params: Record<string, string | number | boolean | undefined | null>): string {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
        if (v === undefined || v === null) continue;
        sp.set(k, String(v));
    }
    return sp.toString();
}

function csv(xs: readonly string[] | null | undefined): string | undefined {
    if (xs === undefined) return undefined;
    if (xs === null) return undefined;
    return xs.join(',');
}

// `allowed` is the set of wire keys the column declares in filter_spec. A
// column that does not declare a parameter answers 400 for it, so an undeclared
// field is dropped here rather than sent and rejected. Passing no list at all
// sends everything set, which is only correct for the activity endpoint.
export function gatedQuery(
    filters: Partial<BoardFilters> | undefined,
    allowed?: readonly string[] | null,
): Record<string, string | number | undefined> {
    if (!filters) return {};
    const out: Record<string, string | number | undefined> = {};
    for (const field of GATED_FIELDS) {
        const wire = FILTER_WIRE_KEYS[field];
        if (allowed && !allowed.includes(wire)) continue;
        const value = filters[field];
        if (value === undefined || value === null) continue;
        out[wire] = value as string | number;
    }
    return out;
}

export interface BoardEventsQuery {
    column: string;
    group?: string | null;
    sortBy?: string | null;
    sortDir?: SortDir;
    eventsInclude?: string[] | null;
    eventsExclude?: string[] | null;
    tagsInclude?: string[] | null;
    tagsExclude?: string[] | null;
    tagMatch?: TagMatch;
    filters?: Partial<BoardFilters> | null;
    filterKeys?: readonly string[] | null;
    scoreGroups?: string[];
    cursor?: string | null;
    limit?: number;
    signal?: AbortSignal;
}

export async function fetchBoardSchema(signal?: AbortSignal): Promise<BoardSchema> {
    return apiGet<BoardSchema>(`${API_BASE}/schema`, signal);
}

export async function fetchBoardEvents(q: BoardEventsQuery): Promise<BoardEventsResponse> {
    const url = `${API_BASE}/events?${qs({
        column: q.column,
        group: q.group ?? undefined,
        sort_by: q.sortBy ?? undefined,
        sort_dir: q.sortDir ?? undefined,
        event_include: csv(q.eventsInclude),
        event_exclude: csv(q.eventsExclude),
        tags_include: csv(q.tagsInclude),
        tags_exclude: csv(q.tagsExclude),
        tag_match: q.tagMatch ?? undefined,
        score_groups: csv(q.scoreGroups),
        cursor: q.cursor ?? undefined,
        limit: q.limit ?? undefined,
        ...gatedQuery(q.filters ?? undefined, q.filterKeys),
    })}`;
    return apiGet<BoardEventsResponse>(url, q.signal);
}

export const MAX_RANK_LIMIT = 200;

export interface BoardRankQuery {
    column: string;
    group?: string | null;
    sortBy?: string | null;
    sortDir?: SortDir;
    eventsInclude?: string[] | null;
    eventsExclude?: string[] | null;
    tagsInclude?: string[] | null;
    tagsExclude?: string[] | null;
    tagMatch?: TagMatch;
    filters?: Partial<BoardFilters> | null;
    filterKeys?: readonly string[] | null;
    limit?: number;
    signal?: AbortSignal;
}

export async function fetchBoardRank(q: BoardRankQuery): Promise<BoardRankResponse> {
    const url = `${API_BASE}/rank?${qs({
        column: q.column,
        group: q.group ?? undefined,
        sort_by: q.sortBy ?? undefined,
        sort_dir: q.sortDir ?? undefined,
        event_include: csv(q.eventsInclude),
        event_exclude: csv(q.eventsExclude),
        tags_include: csv(q.tagsInclude),
        tags_exclude: csv(q.tagsExclude),
        tag_match: q.tagMatch ?? undefined,
        limit: q.limit ?? undefined,
        ...gatedQuery(q.filters ?? undefined, q.filterKeys),
    })}`;
    return apiGet<BoardRankResponse>(url, q.signal);
}

export async function fetchBoardEventsByIds(
    ids: string[],
    signal?: AbortSignal,
): Promise<BoardEventsByIdsResponse> {
    if (ids.length === 0) return { events: [] };
    const url = `${API_BASE}/events/by-ids?${qs({ ids: ids.slice(0, MAX_EVENT_IDS).join(',') })}`;
    return apiGet<BoardEventsByIdsResponse>(url, signal);
}

export async function fetchBoardScores(
    eventIds: string[],
    groups: string[],
    maxAgeDays?: number | null,
    signal?: AbortSignal,
): Promise<BoardScoresResponse> {
    if (eventIds.length === 0 || groups.length === 0) {
        return { groups, scores: [], index: [] };
    }
    const url = `${API_BASE}/scores?${qs({
        event_ids: eventIds.slice(0, MAX_EVENT_IDS).join(','),
        groups: groups.join(','),
        max_age_days: maxAgeDays ?? undefined,
    })}`;
    return apiGet<BoardScoresResponse>(url, signal);
}

export async function fetchBoardTraders(
    marketId: string,
    group: string,
    signal?: AbortSignal,
): Promise<BoardTradersResponse> {
    const url = `${API_BASE}/traders?${qs({ market_id: marketId, group })}`;
    return apiGet<BoardTradersResponse>(url, signal);
}

export async function fetchBoardSeries(
    marketIds: string[],
    window: string,
    resolution?: string | null,
    signal?: AbortSignal,
): Promise<BoardSeriesResponse> {
    if (marketIds.length === 0) {
        return { window, resolution: resolution ?? '', series: [] };
    }
    const url = `${API_BASE}/series?${qs({
        market_ids: marketIds.slice(0, MAX_SERIES_MARKETS).join(','),
        window,
        resolution: resolution ?? undefined,
    })}`;
    return apiGet<BoardSeriesResponse>(url, signal);
}

export interface BoardActivityQuery {
    eventIds: string[];
    filters?: Partial<BoardFilters> | null;
    filterKeys?: readonly string[] | null;
    signal?: AbortSignal;
}

const EMPTY_ACTIVITY: BoardActivityResponse = {
    scope: 'market',
    metric_mode: 'absolute',
    unit: 'pp',
    windows: {},
    filters: {},
    activity: [],
};

export async function fetchBoardActivity(
    q: BoardActivityQuery,
): Promise<BoardActivityResponse> {
    if (q.eventIds.length === 0) return EMPTY_ACTIVITY;
    const url = `${API_BASE}/activity?${qs({
        event_ids: q.eventIds.slice(0, MAX_ACTIVITY_EVENT_IDS).join(','),
        ...gatedQuery(q.filters ?? undefined, q.filterKeys),
    })}`;
    return apiGet<BoardActivityResponse>(url, q.signal);
}

export interface BoardGroupStatsQuery {
    group: string;
    window?: string;
    eventsInclude?: string[] | null;
    eventsExclude?: string[] | null;
    tagsInclude?: string[] | null;
    tagsExclude?: string[] | null;
    tagMatch?: TagMatch;
    minCapital?: number | null;
    minTraders?: number | null;
    maxAgeDays?: number | null;
    bucketLimit?: number;
    topLimit?: number;
    signal?: AbortSignal;
}

export async function fetchGroupStats(
    q: BoardGroupStatsQuery,
): Promise<BoardGroupStatsResponse> {
    const url = `${API_BASE}/group-stats?${qs({
        group: q.group,
        window: q.window ?? undefined,
        event_include: csv(q.eventsInclude),
        event_exclude: csv(q.eventsExclude),
        tags_include: csv(q.tagsInclude),
        tags_exclude: csv(q.tagsExclude),
        tag_match: q.tagMatch ?? undefined,
        min_capital: q.minCapital ?? undefined,
        min_traders: q.minTraders ?? undefined,
        max_age_days: q.maxAgeDays ?? undefined,
        bucket_limit: q.bucketLimit ?? undefined,
        top_limit: q.topLimit ?? undefined,
    })}`;
    return apiGet<BoardGroupStatsResponse>(url, q.signal);
}